import {
  getFulfillmentChannelConstraintOperations,
  getFulfillmentWindowOperations,
  makeRequiresActivationConstraintOperations,
  getAwardRestrictionsOperations,
  getPaymentMethodConstraintOperations,
} from '@occc/feat-constraints';
import {
  getLimitsInConstraintOperations,
  LimitType,
  getProgressPerTransactionLimitOperations,
  getProgressPerPeriodLimitOperations,
} from '@occc/feat-limits';
import { AwardType, GetConfigureFormOperations, Operation } from '@occc-app/offerHelpers/types';

import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getOfferFulfillmentPriorityUpdateOperations } from '@occc-app/offerHelpers/operations/getOfferFulfillmentPriorityUpdateOperations';
import { getEarningMechanismOperations } from './earningMechanismOperations';
import { PunchcardConfigureFormValues } from '../../../types';
import { getOfferCustomDataOperation } from '../../../../../offerHelpers/operations/getOfferCustomDataOperation';

export const getConfigureFormOperations: GetConfigureFormOperations<
  PunchcardConfigureFormValues
> = (offerId, offer, values, dirtyFields) => {
  const earningMechanismOperations = getEarningMechanismOperations(offerId, values, dirtyFields);

  // using delete and create instead of update
  const activationConstraintOperations = makeRequiresActivationConstraintOperations(
    offerId,
    values.activation,
    dirtyFields,
    offer,
  );
  const activationLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.activation,
    dirtyFields,
    LimitType.Activation,
  );
  const options = offer ? { isTransactionFrequencyLimitSupported: false, offer } : undefined;

  const awardCountLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
    options,
  );

  const progressPerPeriodLimitOperations = getProgressPerPeriodLimitOperations(
    offerId,
    values.awardCount,
    dirtyFields,
  );

  const progressPerTransactionLimitOperations = getProgressPerTransactionLimitOperations(
    offerId,
    values.awardCount,
    dirtyFields,
  );

  const fulfillmentWindowOperations = getFulfillmentWindowOperations(
    offerId,
    values.fulfillmentWindow,
    dirtyFields,
  );

  const paymentMethodConstraintOperations = getPaymentMethodConstraintOperations(
    offerId,
    offer,
    values,
    dirtyFields,
  );

  const fulfillmentChannelConstraintOperations = getFulfillmentChannelConstraintOperations(
    offerId,
    offer,
    values,
  );

  const awardRestrictionsOperations = offer
    ? getAwardRestrictionsOperations({
        offer,
        values: values.awardRestrictions,
        dirtyFields,
        forceDelete: values.awardType !== AwardType.AwardVouchers,
      })
    : [];

  const additionalOperations: Operation[] = [];

  additionalOperations.push(
    ...getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields),
  );

  additionalOperations.push(
    ...getOfferFulfillmentPriorityUpdateOperations(offerId, values, dirtyFields),
  );

  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });
  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }

  // ORDER is important
  // Can not mix awardCount limits and constraint

  return values.activation.isEnabled
    ? [
        ...earningMechanismOperations,
        ...fulfillmentWindowOperations,
        ...awardCountLimitsOperations,
        ...progressPerPeriodLimitOperations,
        ...progressPerTransactionLimitOperations,
        ...activationConstraintOperations,
        ...activationLimitsOperations,
        ...paymentMethodConstraintOperations,
        ...fulfillmentChannelConstraintOperations,
        ...awardRestrictionsOperations,
        ...additionalOperations,
      ]
    : [
        ...earningMechanismOperations,
        ...fulfillmentWindowOperations,
        ...activationConstraintOperations,
        ...activationLimitsOperations,
        ...awardCountLimitsOperations,
        ...progressPerPeriodLimitOperations,
        ...progressPerTransactionLimitOperations,
        ...paymentMethodConstraintOperations,
        ...fulfillmentChannelConstraintOperations,
        ...awardRestrictionsOperations,
        ...additionalOperations,
      ];
};
